﻿import { hasDataPage, qs } from "../core/dom";
import { highlightCode, preloadShiki } from "../lib/shiki-highlighter";

let jsonRenderVersion = 0;
let nodeIdCounter = 0;

export async function initJsonViewerPage() {
    if (!hasDataPage("json-viewer")) return;

    const page = {
        jsonEditor: qs("[data-json-editor]"),
        jsonInput: qs("[data-json-input]"),
        jsonHighlight: qs("[data-json-highlight]"),

        jsonTreeWrap: qs("[data-json-tree-wrap]"),
        jsonTree: qs("[data-json-tree]"),
        jsonStatus: qs("[data-json-status]"),
        jsonEmptyState: qs("[data-json-empty-state]"),
        jsonErrorState: qs("[data-json-error-state]"),
        jsonErrorMessage: qs("[data-json-error-message]"),

        validateBtn: qs("[data-validate-json]"),
        formatBtn: qs("[data-format-json]"),
        minifyBtn: qs("[data-minify-json]"),
        expandAllBtn: qs("[data-expand-all]"),
        collapseAllBtn: qs("[data-collapse-all]"),
        copyBtn: qs("[data-copy-json]"),
        downloadBtn: qs("[data-download-json]"),
        loadExampleBtn: qs("[data-load-example]"),
        clearBtn: qs("[data-clear-json]"),

        leftViewTabs: Array.from(document.querySelectorAll("[data-left-view-tab]")),
        rightViewTabs: Array.from(document.querySelectorAll("[data-right-view-tab]")),
        leftViews: Array.from(document.querySelectorAll("[data-left-view]")),
        rightViews: Array.from(document.querySelectorAll("[data-right-view]")),

        leftFoldedView: qs("[data-left-folded-view]"),
        rightFoldedView: qs("[data-right-folded-view]"),

        jsonSearch: qs("[data-json-search]"),
        jsonStats: qs("[data-json-stats]"),
        collapseLevelButtons: Array.from(document.querySelectorAll("[data-collapse-level]")),
        jsonSearchClear: qs("[data-json-search-clear]"),
    };

    bindEvents(page);

    await preloadShiki();
    await renderInitialState(page);
}

function bindViewTabs(page) {
    page.leftViewTabs.forEach(tab => {
        tab.addEventListener("click", () => {
            const target = tab.dataset.leftViewTab;

            page.leftViewTabs.forEach(x => x.classList.toggle("active", x === tab));
            page.leftViews.forEach(view => {
                const isActive = view.dataset.leftView === target;
                view.hidden = !isActive;
                view.classList.toggle("active", isActive);
            });
        });
    });

    page.rightViewTabs.forEach(tab => {
        tab.addEventListener("click", () => {
            const target = tab.dataset.rightViewTab;

            page.rightViewTabs.forEach(x => x.classList.toggle("active", x === tab));
            page.rightViews.forEach(view => {
                const isActive = view.dataset.rightView === target;
                view.hidden = !isActive;
                view.classList.toggle("active", isActive);
            });
        });
    });
}

function toggleSearchClear(page, value) {
    if (!page.jsonSearchClear) return;
    page.jsonSearchClear.hidden = !value;
}

function bindEvents(page) {
    page.jsonInput?.addEventListener("input", debounce(async () => {
        autoResizeEditor(page);
        await renderJsonInput(page);
        validateAndRenderTree(page, { silent: true });
    }, 120));

    page.jsonSearch?.addEventListener("input", debounce(() => {
        const value = page.jsonSearch.value.trim();
        toggleSearchClear(page, value);
        applyTreeSearch(page, value);
    }, 120));

    page.jsonSearchClear?.addEventListener("click", () => {
        page.jsonSearch.value = "";
        toggleSearchClear(page, "");
        applyTreeSearch(page, "");
        page.jsonSearch.focus();
    });

    page.collapseLevelButtons.forEach(btn => {
        btn.addEventListener("click", () => {
            const level = Number(btn.dataset.collapseLevel || 1);
            collapseTreeToLevel(page, level);
            collapseFoldedToLevel(page.leftFoldedView, level);
            collapseFoldedToLevel(page.rightFoldedView, level);
        });
    });

    page.jsonInput?.addEventListener("scroll", () => syncEditorScroll(page));
    //page.jsonInput?.addEventListener("keydown", (e) => handleEditorTab(e));
    page.jsonInput?.addEventListener("keydown", async (e) => {
        const handled = handleJsonEditorKeydown(e, page);
        if (handled) {
            autoResizeEditor(page);
            await renderJsonInput(page);
            validateAndRenderTree(page, { silent: true });
        }
    });

    page.validateBtn?.addEventListener("click", () => validateAndRenderTree(page));
    page.formatBtn?.addEventListener("click", async () => formatJson(page));
    page.minifyBtn?.addEventListener("click", async () => minifyJson(page));
    page.expandAllBtn?.addEventListener("click", () => {
        setAllTreeNodes(page, true);
        setAllFoldNodes(page.leftFoldedView, true);
        setAllFoldNodes(page.rightFoldedView, true);
    });

    page.collapseAllBtn?.addEventListener("click", () => {
        setAllTreeNodes(page, false);
        setAllFoldNodes(page.leftFoldedView, false);
        setAllFoldNodes(page.rightFoldedView, false);
    });
    page.copyBtn?.addEventListener("click", async () => copyJson(page));
    page.downloadBtn?.addEventListener("click", () => downloadJson(page));
    page.loadExampleBtn?.addEventListener("click", async () => loadExample(page));
    page.clearBtn?.addEventListener("click", async () => clearJson(page));

    document.addEventListener("click", (e) => {
        const toggle = e.target.closest("[data-theme-toggle]");
        if (!toggle) return;

        setTimeout(async () => {
            await renderJsonInput(page);
        }, 60);
    });

    bindViewTabs(page);
}

async function renderInitialState(page) {
    autoResizeEditor(page);
    await renderJsonInput(page);
    showEmptyState(page);
    toggleSearchClear(page, page.jsonSearch?.value?.trim() || "");
}

async function renderJsonInput(page) {
    if (!page.jsonHighlight || !page.jsonInput) return;

    const currentVersion = ++jsonRenderVersion;
    const code = page.jsonInput.value || " ";

    const html = await highlightCode(code, "json");

    if (currentVersion !== jsonRenderVersion) return;

    page.jsonHighlight.innerHTML = html;
    syncEditorScroll(page);
}

function autoResizeEditor(page) {
    const textarea = page.jsonInput;
    const highlight = page.jsonHighlight;

    if (!textarea || !highlight) return;

    textarea.style.height = "auto";
    const newHeight = Math.max(textarea.scrollHeight, 520);

    textarea.style.height = `${newHeight}px`;
    highlight.style.height = `${newHeight}px`;
}

function syncEditorScroll(page) {
    const textarea = page.jsonInput;
    const highlightHost = page.jsonHighlight;

    if (!textarea || !highlightHost) return;

    highlightHost.scrollTop = textarea.scrollTop;
    highlightHost.scrollLeft = textarea.scrollLeft;
}

function handleEditorTab(e) {
    if (e.key !== "Tab") return;

    e.preventDefault();

    const textarea = e.currentTarget;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const value = textarea.value;

    textarea.value = value.substring(0, start) + "    " + value.substring(end);
    textarea.selectionStart = textarea.selectionEnd = start + 4;

    textarea.dispatchEvent(new Event("input", { bubbles: true }));
}

function handleJsonEditorKeydown(e, page) {
    const textarea = page.jsonInput;
    if (!textarea) return false;

    if (e.key === "Tab") {
        e.preventDefault();
        insertAtSelection(textarea, "    ");
        return true;
    }

    if (e.key === "{") {
        e.preventDefault();
        wrapPair(textarea, "{", "}");
        return true;
    }

    if (e.key === "[") {
        e.preventDefault();
        wrapPair(textarea, "[", "]");
        return true;
    }

    if (e.key === '"') {
        e.preventDefault();
        handleQuotePair(textarea);
        return true;
    }

    if (e.key === "}") {
        if (shouldSkipClosing(textarea, "}")) {
            e.preventDefault();
            moveCaretForward(textarea);
            return true;
        }
        return false;
    }

    if (e.key === "]") {
        if (shouldSkipClosing(textarea, "]")) {
            e.preventDefault();
            moveCaretForward(textarea);
            return true;
        }
        return false;
    }

    if (e.key === "Enter") {
        e.preventDefault();
        handleJsonEnter(textarea);
        return true;
    }

    return false;
}

function wrapPair(textarea, open, close) {
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const value = textarea.value;
    const selected = value.slice(start, end);

    if (selected) {
        textarea.value = value.slice(0, start) + open + selected + close + value.slice(end);
        textarea.selectionStart = start + 1;
        textarea.selectionEnd = end + 1;
        return;
    }

    textarea.value = value.slice(0, start) + open + close + value.slice(end);
    textarea.selectionStart = textarea.selectionEnd = start + 1;
}

function handleQuotePair(textarea) {
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const value = textarea.value;
    const nextChar = value[start];

    if (nextChar === '"') {
        textarea.selectionStart = textarea.selectionEnd = start + 1;
        return;
    }

    const selected = value.slice(start, end);

    if (selected) {
        textarea.value = value.slice(0, start) + '"' + selected + '"' + value.slice(end);
        textarea.selectionStart = start + 1;
        textarea.selectionEnd = end + 1;
        return;
    }

    textarea.value = value.slice(0, start) + '""' + value.slice(end);
    textarea.selectionStart = textarea.selectionEnd = start + 1;
}

function shouldSkipClosing(textarea, closingChar) {
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;

    if (start !== end) return false;

    return textarea.value[start] === closingChar;
}

function moveCaretForward(textarea) {
    const pos = textarea.selectionStart;
    textarea.selectionStart = textarea.selectionEnd = pos + 1;
}

function handleJsonEnter(textarea) {
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const value = textarea.value;

    const before = value.slice(0, start);
    const after = value.slice(end);

    const currentLineStart = before.lastIndexOf("\n") + 1;
    const currentLine = before.slice(currentLineStart);
    const baseIndent = (currentLine.match(/^\s*/) || [""])[0];

    const prevChar = before.trimEnd().slice(-1);
    const nextChar = after.trimStart().charAt(0);

    // {...} ya da [...] arasında enter
    if (
        (prevChar === "{" && nextChar === "}") ||
        (prevChar === "[" && nextChar === "]")
    ) {
        const innerIndent = baseIndent + "    ";
        const insertion = `\n${innerIndent}\n${baseIndent}`;

        textarea.value = before + insertion + after;
        textarea.selectionStart = textarea.selectionEnd = start + innerIndent.length + 1;
        return;
    }

    // satır sonu { veya [ ile bitiyorsa bir seviye indent ekle
    if (prevChar === "{" || prevChar === "[") {
        const insertion = `\n${baseIndent}    `;
        textarea.value = before + insertion + after;
        textarea.selectionStart = textarea.selectionEnd = start + insertion.length;
        return;
    }

    // normal enter
    const insertion = `\n${baseIndent}`;
    textarea.value = before + insertion + after;
    textarea.selectionStart = textarea.selectionEnd = start + insertion.length;
}

function insertAtSelection(textarea, text) {
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const value = textarea.value;

    textarea.value = value.slice(0, start) + text + value.slice(end);
    textarea.selectionStart = textarea.selectionEnd = start + text.length;
}

function validateAndRenderTree(page, options = {}) {
    const raw = page.jsonInput?.value?.trim();

    if (!raw) {
        showEmptyState(page);
        renderFoldedViews(page, null);
        return;
    }

    try {
        const parsed = JSON.parse(raw);
        renderTree(page, parsed);
        renderFoldedViews(page, parsed);
        setStatus(page, "Valid", "is-valid");
    } catch (error) {
        showErrorState(page, error?.message || "Invalid JSON");
        renderFoldedViews(page, null, error?.message || "Invalid JSON");
        setStatus(page, "Invalid", "is-error");

        if (!options.silent) {
            console.error(error);
        }
    }
}

function renderFoldedViews(page, parsed, errorMessage = "") {
    renderFoldedJson(page.leftFoldedView, parsed, errorMessage);
    renderFoldedJson(page.rightFoldedView, parsed, errorMessage);
}

function renderFoldedJson(container, parsed, errorMessage = "") {
    if (!container) return;

    if (parsed == null) {
        if (errorMessage) {
            container.innerHTML = `
                <div class="fold-code-empty is-error">
                    <i class="fa-regular fa-circle-exclamation"></i>
                    <span>${escapeHtml(errorMessage)}</span>
                </div>
            `;
        } else {
            container.innerHTML = `
                <div class="fold-code-empty">
                    <i class="fa-regular fa-brackets-curly"></i>
                    <span>No JSON to display.</span>
                </div>
            `;
        }
        return;
    }

    container.innerHTML = "";
    container.appendChild(buildFoldNode(parsed, 0, true, true));
    bindFoldCodeEvents(container);
}

function buildFoldNode(value, level = 0, expanded = true, isRoot = false, key = null) {
    const type = getValueType(value);
    const wrap = document.createElement("div");
    wrap.className = "fold-node";
    wrap.dataset.level = String(level);

    if (type !== "object" && type !== "array") {
        const line = document.createElement("div");
        line.className = "fold-line";

        line.appendChild(buildIndent(level));

        if (!isRoot && key !== null) {
            line.appendChild(buildFoldKey(key));
            line.appendChild(buildToken(": "));
        }

        line.appendChild(buildPrimitiveToken(value));
        wrap.appendChild(line);
        return wrap;
    }

    const id = `fold-${++nodeIdCounter}`;
    const openToken = type === "object" ? "{" : "[";
    const closeToken = type === "object" ? "}" : "]";
    const size = type === "object" ? Object.keys(value).length : value.length;

    const head = document.createElement("div");
    head.className = "fold-line";

    head.appendChild(buildIndent(level));

    const toggle = document.createElement("button");
    toggle.type = "button";
    toggle.className = "fold-toggle";
    toggle.dataset.foldToggle = id;
    toggle.innerHTML = expanded
        ? `<i class="fa-regular fa-chevron-down"></i>`
        : `<i class="fa-regular fa-chevron-right"></i>`;
    head.appendChild(toggle);

    if (!isRoot && key !== null) {
        head.appendChild(buildFoldKey(key));
        head.appendChild(buildToken(": "));
    }

    const open = document.createElement("span");
    open.className = "fold-token";
    open.textContent = openToken;
    head.appendChild(open);

    const meta = document.createElement("span");
    meta.className = "fold-meta";
    meta.textContent = expanded
        ? ""
        : ` ${type === "object" ? "Object" : "Array"}(${size}) `;
    head.appendChild(meta);

    if (!expanded) {
        const closeInline = document.createElement("span");
        closeInline.className = "fold-token";
        closeInline.textContent = closeToken;
        head.appendChild(closeInline);
    }

    wrap.appendChild(head);

    const body = document.createElement("div");
    body.className = "fold-children";
    body.dataset.foldBody = id;
    body.hidden = !expanded;

    const entries = type === "object"
        ? Object.entries(value)
        : value.map((x, i) => [`[${i}]`, x]);

    entries.forEach((entry, index) => {
        const [childKey, childValue] = entry;
        const child = buildFoldNode(childValue, level + 1, level < 1, false, childKey);

        // trailing comma
        const lastLine = child.querySelector(".fold-line:last-child, .fold-closing:last-child");
        if (lastLine && index < entries.length - 1) {
            const comma = document.createElement("span");
            comma.className = "fold-token";
            comma.textContent = ",";
            lastLine.appendChild(comma);
        }

        body.appendChild(child);
    });

    const closing = document.createElement("div");
    closing.className = "fold-closing";
    closing.appendChild(buildIndent(level));
    closing.appendChild(buildToken(closeToken));
    body.appendChild(closing);

    wrap.appendChild(body);
    return wrap;
}

function bindFoldCodeEvents(container) {
    container.querySelectorAll("[data-fold-toggle]").forEach(btn => {
        btn.addEventListener("click", () => {
            const id = btn.dataset.foldToggle;
            const body = container.querySelector(`[data-fold-body="${id}"]`);
            if (!body) return;

            const isHidden = body.hidden;
            body.hidden = !isHidden;
            btn.innerHTML = isHidden
                ? `<i class="fa-regular fa-chevron-down"></i>`
                : `<i class="fa-regular fa-chevron-right"></i>`;

            const meta = btn.parentElement?.querySelector(".fold-meta");
            if (meta) {
                meta.textContent = isHidden ? "" : meta.textContent || " ";
            }

            const line = btn.parentElement;
            if (!line) return;

            const existingClose = line.querySelector(".fold-inline-close");
            if (existingClose) existingClose.remove();

            const openToken = line.querySelector(".fold-token");
            const isObject = openToken?.textContent === "{";
            const closeChar = isObject ? "}" : "]";

            if (!isHidden) {
                const bodyChildren = body.children.length > 0 ? body.children.length - 1 : 0;
                if (meta) {
                    meta.textContent = ` ${isObject ? "Object" : "Array"}(${bodyChildren}) `;
                }

                const closeInline = document.createElement("span");
                closeInline.className = "fold-token fold-inline-close";
                closeInline.textContent = closeChar;
                line.appendChild(closeInline);
            } else {
                if (meta) meta.textContent = "";
            }
        });
    });
}

function buildIndent(level) {
    const indent = document.createElement("span");
    indent.className = "fold-indent";
    indent.style.width = `${level * 20}px`;
    return indent;
}

function buildFoldKey(key) {
    const keyEl = document.createElement("span");
    keyEl.className = "fold-key";
    keyEl.textContent = typeof key === "string" && key.startsWith("[") ? key : `"${key}"`;
    return keyEl;
}

function buildPrimitiveToken(value) {
    const type = getValueType(value);
    const token = document.createElement("span");

    if (type === "string") {
        token.className = "fold-value is-string";
        token.textContent = `"${value}"`;
        return token;
    }

    if (type === "number") {
        token.className = "fold-value is-number";
        token.textContent = String(value);
        return token;
    }

    if (type === "boolean") {
        token.className = "fold-value is-boolean";
        token.textContent = String(value);
        return token;
    }

    token.className = "fold-value is-null";
    token.textContent = "null";
    return token;
}

function buildToken(text) {
    const token = document.createElement("span");
    token.className = "fold-token";
    token.textContent = text;
    return token;
}

function escapeHtml(value) {
    return value
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;")
        .replaceAll("'", "&#39;");
}

async function formatJson(page) {
    const raw = page.jsonInput?.value?.trim();
    if (!raw) return;

    try {
        const parsed = JSON.parse(raw);
        page.jsonInput.value = JSON.stringify(parsed, null, 2);
        autoResizeEditor(page);
        await renderJsonInput(page);
        renderTree(page, parsed);
        setStatus(page, "Formatted", "is-valid");
    } catch (error) {
        showErrorState(page, error?.message || "Invalid JSON");
        setStatus(page, "Invalid", "is-error");
    }
}

async function minifyJson(page) {
    const raw = page.jsonInput?.value?.trim();
    if (!raw) return;

    try {
        const parsed = JSON.parse(raw);
        page.jsonInput.value = JSON.stringify(parsed);
        autoResizeEditor(page);
        await renderJsonInput(page);
        renderTree(page, parsed);
        setStatus(page, "Minified", "is-valid");
    } catch (error) {
        showErrorState(page, error?.message || "Invalid JSON");
        setStatus(page, "Invalid", "is-error");
    }
}

function updateJsonStats(page, data) {
    if (!page.jsonStats) return;

    const stats = {
        keys: 0,
        objects: 0,
        arrays: 0,
        values: 0
    };

    walkJson(data, stats);

    page.jsonStats.textContent = `${stats.keys} keys · ${stats.objects} objects · ${stats.arrays} arrays · ${stats.values} values`;
}

function walkJson(value, stats) {
    const type = getValueType(value);

    if (type === "object") {
        stats.objects++;
        Object.entries(value).forEach(([key, child]) => {
            stats.keys++;
            walkJson(child, stats);
        });
        return;
    }

    if (type === "array") {
        stats.arrays++;
        value.forEach(child => walkJson(child, stats));
        return;
    }

    stats.values++;
}

function renderTree(page, data) {
    nodeIdCounter = 0;

    page.jsonEmptyState.hidden = true;
    page.jsonErrorState.hidden = true;
    page.jsonTree.hidden = false;

    const root = buildTreeNode({
        key: null,
        value: data,
        level: 0,
        expanded: true,
        isRoot: true
    });

    page.jsonTree.innerHTML = "";
    page.jsonTree.appendChild(root);

    bindTreeEvents(page);
    updateJsonStats(page, data);
    applyTreeSearch(page, page.jsonSearch?.value?.trim() || "");
}

function buildSearchText(key, value) {
    const parts = [];

    if (key !== null && key !== undefined) {
        parts.push(String(key).toLowerCase());
    }

    const type = getValueType(value);

    if (type === "string" || type === "number" || type === "boolean") {
        parts.push(String(value).toLowerCase());
    }

    if (type === "null") {
        parts.push("null");
    }

    if (type === "object") {
        parts.push("object");
    }

    if (type === "array") {
        parts.push("array");
    }

    return parts.join(" ");
}

function applyTreeSearch(page, term) {
    const query = (term || "").trim().toLowerCase();

    const nodes = Array.from(page.jsonTree?.querySelectorAll(".json-node") || []);
    if (!nodes.length) return;

    // reset
    nodes.forEach(node => {
        node.classList.remove("is-search-hit", "is-search-hidden");
    });

    if (!query) {
        nodes.forEach(node => node.classList.remove("is-search-hidden"));
        return;
    }

    const matchedNodes = new Set();

    nodes.forEach(node => {
        const text = node.dataset.searchText || "";
        if (text.includes(query)) {
            matchedNodes.add(node);
        }
    });

    // parent chain'i ekle
    const visibleNodes = new Set();

    matchedNodes.forEach(node => {
        let current = node;

        while (current) {
            visibleNodes.add(current);

            current = current.parentElement?.closest(".json-node");
        }
    });

    // apply
    nodes.forEach(node => {
        const isVisible = visibleNodes.has(node);

        node.classList.toggle("is-search-hidden", !isVisible);
        node.classList.toggle("is-search-hit", matchedNodes.has(node));

        // parentları otomatik aç
        if (isVisible) {
            const body = node.querySelector(":scope > .json-node__children");
            const toggle = node.querySelector(":scope > .json-node__head [data-toggle-node]");

            if (body && toggle && !toggle.classList.contains("is-empty")) {
                body.hidden = false;
                toggle.innerHTML = `<i class="fa-regular fa-chevron-down"></i>`;
            }
        }
    });
}

function buildTreeNode({ key, value, level, expanded = true, isRoot = false, path = "$" })
{
    const node = document.createElement("div");
    node.className = "json-node";
    node.dataset.level = String(level);
    node.dataset.searchText = buildSearchText(key, value);
    node.dataset.path = path;

    const valueType = getValueType(value);
    const nodeId = `json-node-${++nodeIdCounter}`;

    const header = document.createElement("div");
    header.className = "json-node__head";

    const left = document.createElement("div");
    left.className = "json-node__left";

    const toggle = document.createElement("button");
    toggle.type = "button";
    toggle.className = "json-node__toggle";
    toggle.dataset.toggleNode = nodeId;

    const hasChildren = valueType === "object" || valueType === "array";

    if (!hasChildren) {
        toggle.classList.add("is-empty");
        toggle.innerHTML = `<i class="fa-regular fa-minus"></i>`;
    } else {
        toggle.innerHTML = expanded
            ? `<i class="fa-regular fa-chevron-down"></i>`
            : `<i class="fa-regular fa-chevron-right"></i>`;
    }

    left.appendChild(toggle);

    if (!isRoot && key !== null) {
        const keyEl = document.createElement("span");
        keyEl.className = "json-node__key";
        keyEl.textContent = key;
        left.appendChild(keyEl);

        const colon = document.createElement("span");
        colon.className = "json-node__colon";
        colon.textContent = ":";
        left.appendChild(colon);
    }

    const valuePreview = document.createElement("div");
    valuePreview.className = "json-node__value";

    if (valueType === "object") {
        valuePreview.innerHTML = `<span class="json-type-badge is-object">{ } Object</span>`;
    } else if (valueType === "array") {
        valuePreview.innerHTML = `<span class="json-type-badge is-array">[ ] Array (${value.length})</span>`;
    } else {
        valuePreview.appendChild(buildValueElement(value));
    }

    const actions = document.createElement("div");
    actions.className = "json-node__actions";

    const copyBtn = document.createElement("button");
    copyBtn.type = "button";
    copyBtn.className = "json-node__copy";
    copyBtn.dataset.copyPath = path;
    copyBtn.title = "Copy path";
    copyBtn.innerHTML = `<i class="fa-regular fa-copy"></i>`;

    actions.appendChild(copyBtn);

    header.appendChild(left);
    header.appendChild(valuePreview);
    header.appendChild(actions);

    node.appendChild(header);

    if (hasChildren) {
        const childrenWrap = document.createElement("div");
        childrenWrap.className = "json-node__children";
        childrenWrap.dataset.nodeBody = nodeId;
        childrenWrap.hidden = !expanded;

        if (valueType === "object") {
            Object.entries(value).forEach(([childKey, childValue]) => {
                childrenWrap.appendChild(buildTreeNode({
                    key: childKey,
                    value: childValue,
                    level: level + 1,
                    expanded: level < 1,
                    path: `${path}.${childKey}`
                }));
            });
        }

        if (valueType === "array") {
            value.forEach((childValue, index) => {
                childrenWrap.appendChild(buildTreeNode({
                    key: `[${index}]`,
                    value: childValue,
                    level: level + 1,
                    expanded: level < 1,
                    path: `${path}[${index}]`
                }));
            });
        }

        node.appendChild(childrenWrap);
    }

    return node;
}

function buildValueElement(value) {
    const type = getValueType(value);
    const span = document.createElement("span");

    if (type === "string") {
        span.className = "json-value json-value--string";
        span.textContent = `"${truncateString(value)}"`;
        span.title = value;
        return span;
    }

    if (type === "number") {
        span.className = "json-value json-value--number";
        span.textContent = String(value);
        return span;
    }

    if (type === "boolean") {
        span.className = "json-value json-value--boolean";
        span.textContent = String(value);
        return span;
    }

    if (type === "null") {
        span.className = "json-value json-value--null";
        span.textContent = "null";
        return span;
    }

    span.className = "json-value";
    span.textContent = String(value);
    return span;
}

function collapseTreeToLevel(page, maxVisibleLevel) {
    page.jsonTree?.querySelectorAll(".json-node").forEach(node => {
        const level = Number(node.dataset.level || 0);
        const body = node.querySelector(":scope > .json-node__children");
        const toggle = node.querySelector(":scope > .json-node__head [data-toggle-node]");

        if (!body || !toggle || toggle.classList.contains("is-empty")) return;

        const expand = level < maxVisibleLevel;
        body.hidden = !expand;
        toggle.innerHTML = expand
            ? `<i class="fa-regular fa-chevron-down"></i>`
            : `<i class="fa-regular fa-chevron-right"></i>`;
    });
}

function collapseFoldedToLevel(container, maxVisibleLevel) {
    if (!container) return;

    container.querySelectorAll(".fold-node").forEach(node => {
        const level = Number(node.dataset.level || 0);
        const body = node.querySelector(":scope > .fold-children");
        const toggle = node.querySelector(":scope > .fold-line [data-fold-toggle]");

        if (!body || !toggle) return;

        const expand = level < maxVisibleLevel;
        body.hidden = !expand;
        toggle.innerHTML = expand
            ? `<i class="fa-regular fa-chevron-down"></i>`
            : `<i class="fa-regular fa-chevron-right"></i>`;
    });

    container.querySelectorAll(".fold-inline-close").forEach(x => x.remove());
}

function bindTreeEvents(page) {
    page.jsonTree.querySelectorAll("[data-toggle-node]").forEach(btn => {
        btn.addEventListener("click", () => {
            if (btn.classList.contains("is-empty")) return;

            const targetId = btn.dataset.toggleNode;
            const body = page.jsonTree.querySelector(`[data-node-body="${targetId}"]`);
            if (!body) return;

            const isHidden = body.hidden;
            body.hidden = !isHidden;
            btn.innerHTML = isHidden
                ? `<i class="fa-regular fa-chevron-down"></i>`
                : `<i class="fa-regular fa-chevron-right"></i>`;
        });
    });

    page.jsonTree.querySelectorAll("[data-copy-path]").forEach(btn => {
        btn.addEventListener("click", async (e) => {
            e.stopPropagation();
            const path = btn.dataset.copyPath || "$";
            await navigator.clipboard.writeText(path);

            btn.classList.add("is-copied");
            setTimeout(() => btn.classList.remove("is-copied"), 900);
        });
    });
}

function setAllTreeNodes(page, expand) {
    page.jsonTree.querySelectorAll("[data-node-body]").forEach(body => {
        body.hidden = !expand;
    });

    page.jsonTree.querySelectorAll("[data-toggle-node]").forEach(btn => {
        if (btn.classList.contains("is-empty")) return;

        btn.innerHTML = expand
            ? `<i class="fa-regular fa-chevron-down"></i>`
            : `<i class="fa-regular fa-chevron-right"></i>`;
    });
}

function setAllFoldNodes(container, expand) {
    if (!container) return;

    container.querySelectorAll("[data-fold-body]").forEach(body => {
        body.hidden = !expand;
    });

    container.querySelectorAll("[data-fold-toggle]").forEach(btn => {
        btn.innerHTML = expand
            ? `<i class="fa-regular fa-chevron-down"></i>`
            : `<i class="fa-regular fa-chevron-right"></i>`;
    });

    container.querySelectorAll(".fold-meta").forEach(meta => {
        if (expand) {
            meta.textContent = "";
        }
    });

    container.querySelectorAll(".fold-inline-close").forEach(x => x.remove());
}

function showEmptyState(page) {
    page.jsonTree.hidden = true;
    page.jsonErrorState.hidden = true;
    page.jsonEmptyState.hidden = false;
    setStatus(page, "Waiting", "");
}

function showErrorState(page, message) {
    page.jsonTree.hidden = true;
    page.jsonEmptyState.hidden = true;
    page.jsonErrorState.hidden = false;
    page.jsonErrorMessage.textContent = message;
}

function setStatus(page, text, stateClass) {
    page.jsonStatus.textContent = text;
    page.jsonStatus.classList.remove("is-valid", "is-error");

    if (stateClass) {
        page.jsonStatus.classList.add(stateClass);
    }
}

async function copyJson(page) {
    const text = page.jsonInput?.value || "";
    await navigator.clipboard.writeText(text);
}

function downloadJson(page) {
    const content = page.jsonInput?.value || "";
    const blob = new Blob([content], { type: "application/json;charset=utf-8" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = "data.json";
    document.body.appendChild(a);
    a.click();
    a.remove();

    setTimeout(() => URL.revokeObjectURL(url), 500);
}

async function loadExample(page) {
    page.jsonInput.value = `{
  "id": 1024,
  "title": "Premium JSON Viewer",
  "isActive": true,
  "tags": ["json", "viewer", "tool", "tree"],
  "@attribute" : "data-json",
  "author": {
    "name": "Mahmut",
    "role": "Assistant",
    "contact": {
      "email": "mahmut@example.com",
      "verified": false
    }
  },
  "stats": {
    "views": 18290,
    "rating": 4.8,
    "nullableValue": null
  },
  "items": [
    {
      "id": 1,
      "name": "Node A"
    },
    {
      "id": 2,
      "name": "Node B"
    }
  ]
}`;

    autoResizeEditor(page);
    await renderJsonInput(page);
    validateAndRenderTree(page);
}

async function clearJson(page) {
    page.jsonInput.value = "";
    autoResizeEditor(page);
    await renderJsonInput(page);
    showEmptyState(page);
}

function getValueType(value) {
    if (value === null) return "null";
    if (Array.isArray(value)) return "array";
    return typeof value === "object" ? "object" : typeof value;
}

function truncateString(value) {
    return value.length > 120 ? `${value.slice(0, 117)}...` : value;
}

function debounce(fn, delay) {
    let timer = null;
    return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn(...args), delay);
    };
}